import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import ManagementHeader from "./managementheader";
import ManagementMobileNav from "./managementmobilenav";
import MobileBottomCta, { QuickConsultSheet } from "./mobilebottomcta";
import ConsultSheet from "./consultsheet";
import { ManagementKey } from "../lib/managementPages";
import { track } from "../lib/mixpanel";

/**
 * SiteChrome — 전 페이지 공용 크롬(관리 페이지 헤더 + 하단 상담 CTA 바 + 상담/빠른상담 시트).
 * 구 header.tsx/header2.tsx가 이 컴포넌트를 렌더하도록 교체되어, 두 헤더를 쓰던 46개
 * 페이지가 무수정으로 /operationmanagement와 동일한 헤더를 갖는다 (원본은 header*_backup.tsx).
 * 기존 페이지들은 fixed 헤더 + 자체 스페이서(.voidHeader) 레이아웃이므로 fixed 변형으로 렌더.
 * /operationmanagement 허브는 이 컴포넌트를 쓰지 않고 자체 헤더/바를 유지한다.
 */

// 하단 상담 CTA 바 제외 경로 (lowercase prefix 매칭) — 헤더는 유지된다.
const BAR_EXCLUDE = [
    "/notification", "/storys", "/storyread", "/mealmenu", "/faq", // 커뮤니티
    "/review",      // 합격생 이야기(/review) + 재원생 후기(/reviews, /reviewsread)
    "/investpage",  // 투자 문의
    "/register",    // 새 상담신청 폼 + 구 등록 폼들 (자체 CTA 보유)
    "/complete", "/wintercomplete", "/winterwaitingregisterfinish", // 신청 완료 페이지
];

const TOPBAR_PATHS = ["/winter", "/2027winterpresentation"];

const SiteChrome: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: "(max-width: 1024px)" });
    const [consultOpen, setConsultOpen] = useState(false);
    const [quickOpen, setQuickOpen] = useState(false);

    const path = location.pathname.toLowerCase();
    // 윈터스쿨 모집(/winter)·펜타킬 설명회(/2027winterpresentation)는 디자인 전용 플로팅 바 + 상단 고정 띠(38px, wintertopbar.tsx)를 쓴다
    // — 정확 일치로만 제외(/winterschool 등 prefix 영향 없음), 헤더는 띠만큼 내린다
    const hasTopbar = TOPBAR_PATHS.includes(path);
    const showBar = !hasTopbar && !BAR_EXCLUDE.some((p) => path.startsWith(p));
    const topOffset = hasTopbar ? 38 : undefined;

    // 구 header2의 'Consultation CTA Clicked' 추적 계승 — 열리는 건 이동 대신 상담·대기 팝업.
    const openConsult = (source: string) => () => {
        track("Consultation CTA Clicked", { location: source });
        setConsultOpen(true);
    };
    // 헤더의 4가지 관리 메뉴 → 허브의 해당 관리로 이동
    const goManagement = (key: ManagementKey) => navigate(`/operationmanagement/${key}`);

    return (
        <>
            {isMobile ? (
                <ManagementMobileNav fixed topOffset={topOffset} onSelect={goManagement} onOpenQuick={() => setQuickOpen(true)} />
            ) : (
                <ManagementHeader fixed topOffset={topOffset} onSelect={goManagement} onOpenConsult={openConsult("header_desktop")} />
            )}
            {showBar && (
                <MobileBottomCta
                    variant={isMobile ? "mobile" : "pc"}
                    onOpenConsult={openConsult(isMobile ? "bottom_bar_mobile" : "bottom_bar_pc")}
                />
            )}
            <ConsultSheet open={consultOpen} onClose={() => setConsultOpen(false)} />
            <QuickConsultSheet open={quickOpen} onClose={() => setQuickOpen(false)} variant={isMobile ? "mobile" : "pc"} />
        </>
    );
};

export default SiteChrome;
